import PropTypes from 'prop-types';
import { values } from 'rambdax';
import {
  DonationGeneralIcon,
  DonationGrant,
  DonationInKind,
  DonationPlanned,
  DonationPledge,
  DonationRecurring,
  DonationRestricted,
  DonationSponsorship,
  DonationTicket,
  DonationTime,
  HighValueIcon,
  LowValueIcon,
  MediumValueIcon,
  PaymentApp,
  PaymentBankWire,
  PaymentCard,
  PaymentCash,
  PaymentCheck,
  PaymentCryptoIcon,
  SoSoValueIcon,
  VeryHighValueIcon,
  VolunteerAdvocacyIcon,
  VolunteerCampaignsIcon,
  VolunteerEventsIcon,
  VolunteerFoundationIcon,
  VolunteerGeneralIcon,
  VolunteerGiftsIcon,
  VolunteerGovernanceIcon,
  VolunteerOperationsIcon,
  VolunteerOtherIcon,
} from '@/assets/images';
import TimeTypeEnum from '@/constants/contributions/TimeTypeEnum';
import ContributionType from '@/constants/ContributionType';
import PaymentType from '@/constants/PaymentType';
import TimeCategory from '@/constants/TimeCategory';
import translate from '@/lib/intl/translate';
import { CompanyTeamMember } from '@/propTypes';

const DONATION_TYPES = {
  GENERAL: {
    value: ContributionType.GENERAL,
    icon: DonationGeneralIcon,
    iconAlt: translate('CONTRIBUTION_GENERAL_ICON_ALT'),
    displayText: translate('DONATION_GENERAL'),
  },
  PLEDGE: {
    value: ContributionType.PLEDGE,
    icon: DonationPledge,
    iconAlt: translate('CONTRIBUTION_PLEDGE_ICON_ALT'),
    displayText: translate('DONATION_PLEDGE'),
  },
  RECURRING: {
    value: ContributionType.RECURRING,
    icon: DonationRecurring,
    iconAlt: translate('CONTRIBUTION_RECURRING_ICON_ALT'),
    displayText: translate('DONATION_RECURRING'),
  },
  RESTRICTED: {
    value: ContributionType.RESTRICTED,
    icon: DonationRestricted,
    iconAlt: translate('CONTRIBUTION_RESTRICTED_ICON_ALT'),
    displayText: translate('DONATION_RESTRICTED'),
  },
  IN_KIND: {
    value: ContributionType.IN_KIND,
    icon: DonationInKind,
    iconAlt: translate('CONTRIBUTION_IN_KIND_ICON_ALT'),
    displayText: translate('DONATION_IN_KIND'),
  },
  PLANNED_GIVING: {
    value: ContributionType.PLANNED_GIVING,
    icon: DonationPlanned,
    iconAlt: translate('CONTRIBUTION_PLANNED_ICON_ALT'),
    displayText: translate('DONATION_PLANNED'),
  },
  TIME: {
    value: ContributionType.TIME,
    icon: DonationTime,
    iconAlt: translate('CONTRIBUTION_TIME_ICON_ALT'),
    displayText: translate('DONATION_TIME'),
  },
  GRANT: {
    value: ContributionType.GRANT,
    icon: DonationGrant,
    iconAlt: translate('CONTRIBUTION_GRANT_ICON_ALT'),
    displayText: translate('DONATION_GRANT'),
  },
  SPONSORSHIP: {
    value: ContributionType.SPONSORSHIP,
    icon: DonationSponsorship,
    iconAlt: translate('CONTRIBUTION_SPONSORSHIP_ICON_ALT'),
    displayText: translate('DONATION_SPONSORSHIP'),
  },
  TICKET: {
    value: ContributionType.TICKET,
    icon: DonationTicket,
    iconAlt: translate('CONTRIBUTION_TICKET_ICON_ALT'),
    displayText: translate('DONATION_TICKET'),
  },
};

const DONATION_PAYMENT_METHODS = [
  {
    value: PaymentType.CASH,
    icon: PaymentCash,
    iconAlt: translate('PAYMENT_CASH_ICON_ALT'),
    text: translate('DONATION_PAYMENT_CASH'),
  },
  {
    value: PaymentType.CHECK,
    icon: PaymentCheck,
    iconAlt: translate('PAYMENT_CHECK_ICON_ALT'),
    text: translate('DONATION_PAYMENT_CHECK'),
  },
  {
    value: PaymentType.CREDIT,
    icon: PaymentCard,
    iconAlt: translate('PAYMENT_CARD_ICON_ALT'),
    text: translate('DONATION_PAYMENT_CREDIT_CARD'),
  },
  {
    value: PaymentType.WIRE,
    icon: PaymentBankWire,
    iconAlt: translate('PAYMENT_BANK_WIRE_ICON_ALT'),
    text: translate('DONATION_PAYMENT_BANK_WIRE'),
  },
  {
    value: PaymentType.APP,
    icon: PaymentApp,
    iconAlt: translate('PAYMENT_APP_ICON_ALT'),
    text: translate('DONATION_PAYMENT_APP'),
  },
  {
    value: PaymentType.CRYPTO,
    icon: PaymentCryptoIcon,
    iconAlt: translate('PAYMENT_CRYPTO_ICON_ALT'),
    text: translate('DONATION_PAYMENT_CRYPTO'),
  },
  {
    value: PaymentType.UNKNOWN,
    text: translate('DONATION_PAYMENT_UNKNOWN'),
  },
];

const DONATION_PLAN_STATUS_ENUM = {
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
};

const RECURRING_CADENCES_ENUM = {
  DAY: 'day',
  WEEK: 'week',
  BIWEEK: 'biweek',
  MONTH: 'month',
  QUARTER: 'quarter',
  SEMIANNUAL: 'semiannual',
  YEAR: 'year',
};

const DONATION_RECURRING_CADENCES = [
  {
    value: RECURRING_CADENCES_ENUM.DAY,
    text: translate('RECURRING_DONATION_DAILY_LABEL'),
  },
  {
    value: RECURRING_CADENCES_ENUM.WEEK,
    text: translate('RECURRING_DONATION_WEEKLY_LABEL'),
  },
  {
    value: RECURRING_CADENCES_ENUM.BIWEEK,
    text: translate('RECURRING_DONATION_BIWEEKLY_LABEL'),
  },
  {
    value: RECURRING_CADENCES_ENUM.MONTH,
    text: translate('RECURRING_DONATION_MONTHLY_LABEL'),
  },
  {
    value: RECURRING_CADENCES_ENUM.QUARTER,
    text: translate('RECURRING_DONATION_QUARTERLY_LABEL'),
  },
  {
    value: RECURRING_CADENCES_ENUM.SEMIANNUAL,
    text: translate('RECURRING_DONATION_SEMIANNUALLY_LABEL'),
  },
  {
    value: RECURRING_CADENCES_ENUM.YEAR,
    text: translate('RECURRING_DONATION_YEARLY_LABEL'),
  },
];

const DONATION_VOLUNTEER_CONFIG = [
  {
    value: TimeCategory.EVENTS,
    icon: VolunteerEventsIcon,
    iconAlt: translate('VOLUNTEER_EVENTS_ICON_ALT'),
    text: translate('DONATION_EVENTS'),
    enum: TimeCategory.EVENTS,
  },
  {
    value: TimeCategory.CAMPAIGNS,
    icon: VolunteerCampaignsIcon,
    iconAlt: translate('VOLUNTEER_CAMPAIGNS_ICON_ALT'),
    text: translate('DONATION_CAMPAIGNS'),
    enum: TimeCategory.CAMPAIGNS,
  },
  {
    value: TimeCategory.GENERAL,
    icon: VolunteerGeneralIcon,
    iconAlt: translate('VOLUNTEER_GENERAL_ICON_ALT'),
    text: translate('DONATION_GENERAL_CORPORATE'),
    enum: TimeCategory.GENERAL,
  },
  {
    value: TimeCategory.GIFTS,
    icon: VolunteerGiftsIcon,
    iconAlt: translate('VOLUNTEER_GIFTS_ICON_ALT'),
    text: translate('DONATION_GIFTS'),
    enum: TimeCategory.GIFTS,
  },
  {
    value: TimeCategory.OPERATIONS,
    icon: VolunteerOperationsIcon,
    iconAlt: translate('VOLUNTEER_OPERATIONS_ICON_ALT'),
    text: translate('DONATION_OPERATIONS'),
    enum: TimeCategory.OPERATIONS,
  },
  {
    value: TimeCategory.GRANTS,
    icon: VolunteerFoundationIcon,
    iconAlt: translate('VOLUNTEER_FOUNDATION_ICON_ALT'),
    text: translate('DONATION_FOUNDATIONS'),
    enum: TimeCategory.GRANTS,
  },
  {
    value: TimeCategory.GOVERNANCE,
    icon: VolunteerGovernanceIcon,
    iconAlt: translate('VOLUNTEER_GOVERNANCE_ICON_ALT'),
    text: translate('DONATION_VOLUNTEERED_GOVERNANCE'),
    enum: TimeCategory.GOVERNANCE,
  },
  {
    value: TimeCategory.ADVOCACY,
    icon: VolunteerAdvocacyIcon,
    iconAlt: translate('VOLUNTEER_ADVOCACY_ICON_ALT'),
    text: translate('DONATION_ADVOCACY'),
    enum: TimeCategory.ADVOCACY,
  },
  {
    value: TimeCategory.OTHER,
    icon: VolunteerOtherIcon,
    iconAlt: translate('VOLUNTEER_OTHER_ICON_ALT'),
    text: translate('DONATION_VOLUNTEERED_OTHER'),
    enum: TimeCategory.OTHER,
  },
];

const DONATION_VALUE_ENUMS = Object.freeze({
  SUPER: 5,
  HIGH: 4,
  MEDIUM: 3,
  SOSO: 2,
  LOW: 1,
});

const DONATION_VALUE_OF_TIME_CONFIG = [
  {
    value: DONATION_VALUE_ENUMS.SUPER,
    icon: VeryHighValueIcon,
    iconAlt: translate('DONATION_VERY_HIGH_VALUE_ICON_ALT'),
    text: translate('DONATION_VERY_HIGH_VALUE'),
  },
  {
    value: DONATION_VALUE_ENUMS.HIGH,
    icon: HighValueIcon,
    iconAlt: translate('DONATION_HIGH_VALUE_ICON_ALT'),
    text: translate('DONATION_HIGH_VALUE'),
  },
  {
    value: DONATION_VALUE_ENUMS.MEDIUM,
    icon: MediumValueIcon,
    iconAlt: translate('DONATION_MODERATE_VALUE_ICON_ALT'),
    text: translate('DONATION_MODERATE_VALUE'),
  },
  {
    value: DONATION_VALUE_ENUMS.SOSO,
    icon: SoSoValueIcon,
    iconAlt: translate('DONATION_LOW_VALUE_ICON_ALT'),
    text: translate('DONATION_LOW_VALUE'),
  },
  {
    value: DONATION_VALUE_ENUMS.LOW,
    icon: LowValueIcon,
    iconAlt: translate('DONATION_MINOR_VALUE_ICON_ALT'),
    text: translate('DONATION_MINOR_VALUE'),
  },
];

const DONATION_TIME_TYPE_CONFIG = [
  {
    value: TimeTypeEnum.MINUTES,
    text: translate('DONATION_MINUTES'),
  },
  {
    value: TimeTypeEnum.HOURS,
    text: translate('DONATION_HOURS'),
  },
  {
    value: TimeTypeEnum.DAYS,
    text: translate('DONATION_DAYS'),
  },
];

const ContributionDetailsType = {
  actualValue: PropTypes.number,
  campaignDisplayName: PropTypes.string,
  constituents: PropTypes.arrayOf(
    PropTypes.shape({ displayName: PropTypes.string }),
  ),
  created: PropTypes.string.isRequired,
  developers: PropTypes.PropTypes.arrayOf(CompanyTeamMember),
  // used for recurring donaitons
  donationId: PropTypes.string.isRequired,
  donationPlan: PropTypes.shape({
    recurringCadenceEnum: PropTypes.oneOf(values(RECURRING_CADENCES_ENUM)),
    recurringStartDateString: PropTypes.string,
  }),
  donationTypeEnum: PropTypes.oneOf([...values(ContributionType), ''])
    .isRequired,
  estimatedValue: PropTypes.number,
  grantData: PropTypes.shape({
    grantName: PropTypes.string,
    restricted: PropTypes.bool,
    restrictionCondition: PropTypes.string,
  }),
  inKind: PropTypes.arrayOf(
    PropTypes.shape({
      inKindItemDescription: PropTypes.string,
      inKindItemQuantity: PropTypes.number,
      inKindTotalValue: PropTypes.number,
    }),
  ),
  notes: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string })),
  paymentTypeEnum: PropTypes.oneOf([...values(PaymentType), '']),
  pledgeData: PropTypes.shape({
    pledgeDueDateString: PropTypes.string,
  }),
  restrictions: PropTypes.arrayOf(
    PropTypes.shape({
      restrictionDescription: PropTypes.string,
    }),
  ),
  timeData: PropTypes.shape({
    timeCategory: PropTypes.oneOf([...values(TimeCategory), '']),
    timeValue: PropTypes.oneOf([...values(DONATION_VALUE_ENUMS), 0]),
    time: PropTypes.number,
    timeUnit: PropTypes.string,
  }),
  updated: PropTypes.string.isRequired,
};

const PLEDGE_LINK_DONATION_TYPES = [
  ContributionType.GENERAL,
  ContributionType.GRANT,
  ContributionType.RECURRING,
  ContributionType.RESTRICTED,
  ContributionType.SPONSORSHIP,
  ContributionType.TICKET,
];

export {
  ContributionDetailsType,
  DONATION_PAYMENT_METHODS,
  DONATION_PLAN_STATUS_ENUM,
  DONATION_RECURRING_CADENCES,
  DONATION_TIME_TYPE_CONFIG,
  DONATION_TYPES,
  DONATION_VALUE_ENUMS,
  DONATION_VALUE_OF_TIME_CONFIG,
  DONATION_VOLUNTEER_CONFIG,
  PLEDGE_LINK_DONATION_TYPES,
  RECURRING_CADENCES_ENUM,
  TimeTypeEnum,
};
